// Install the app (INSTALL.md §4.3, §7; scenario M1): `/account/install`,
// from the account menu, the phone's You sheet, the one offer after a draft
// (`InstallOffer`) and the notification center's iPhone line.
//
// THIS DEVICE FIRST, every other one under it. The steps differ per browser
// and a user-agent is a guess, so the guess leads and the rest are a tap away
// — which also serves somebody on a desktop reading how to do it on their
// phone. Where the browser can raise its own dialog (Chromium), *Install* is
// the first thing on the page; where it cannot, there is no button, only its
// steps (§4.3 rule 2, never a dead button).
//
// ON AN IPHONE OR IPAD THE SIGN-IN SENTENCE COMES FIRST (§1, CLAUDE.md "Say
// what will happen"): before the steps, so it is read at the moment of the
// decision, not discovered at a sign-in screen inside the new icon.
//
// INSIDE THE INSTALLED APP the page says so, and offers what the install was
// for: notifications on this device. B-243.

import { useState } from 'react';
import { Panel } from '../kit/Panel';
import { Icon } from '../kit/Icon';
import { Button, Callout, Stack, Text, Title } from '../kit/ui';
import { PushOnThisDevice } from '../shell/NotificationCenter';
import { isHandheld, isInstalled, thisPlatform } from './platform';
import { promptInstall, useInstallPrompt } from './prompt';
import { GUIDES, SIGN_IN_AGAIN, guideFor, type InstallGuide } from './steps';
import '../styles/screens/account.css';

export function InstallScreen() {
  const platform = thisPlatform();
  const installed = isInstalled();
  const prompt = useInstallPrompt();
  const mine = guideFor(platform);
  const others = GUIDES.filter((g) => g.platform !== mine.platform);

  return (
    <div className="fsp-screen">
      <div className="fsp-widgets fsp-account-column">
        <Stack gap={4}>
          <Title>Install the app</Title>
          <Text tone="secondary" block>
            {isHandheld(platform)
              ? 'It opens full screen from your home screen, without the browser around it,'
              : 'It opens in its own window, without the browser around it,'}{' '}
            and it can send you notifications — on an iPhone or iPad, only the installed app can.
          </Text>
          <Panel title="This device" bodyClass="fsp-install-body">
            {installed ? (
              <Stack gap={3}>
                <Callout tone="good">You're using the installed app on this device.</Callout>
                <PushOnThisDevice />
              </Stack>
            ) : prompt.installedHere ? (
              <Callout tone="good">
                Installed. Open it from your home screen or your apps, and sign in there if it asks.
              </Callout>
            ) : (
              <Guide guide={mine} offerPrompt={prompt.available} />
            )}
          </Panel>
          <Panel title="Another device" bodyClass="fsp-install-body">
            <div className="fsp-install-others">
              {others.map((g) => (
                <details key={g.platform} className="fsp-install-other">
                  <summary>{g.label}</summary>
                  <Guide guide={g} offerPrompt={false} />
                </details>
              ))}
            </div>
          </Panel>
        </Stack>
      </div>
    </div>
  );
}

function Guide({ guide, offerPrompt }: { guide: InstallGuide; offerPrompt: boolean }) {
  const [refused, setRefused] = useState(false);
  const button = guide.canPrompt && offerPrompt && !refused;
  return (
    <Stack gap={3}>
      {guide.signsInAgain ? <Callout tone="warn">{SIGN_IN_AGAIN}</Callout> : null}
      {button ? (
        <Stack gap={2}>
          <div>
            <Button
              variant="primary"
              icon="install"
              onClick={() =>
                void promptInstall().then((outcome) => {
                  if (outcome !== 'accepted') setRefused(true);
                })
              }
            >
              Install
            </Button>
          </div>
          <Text size="xs" tone="secondary" block>
            Or from the browser's own menu:
          </Text>
        </Stack>
      ) : null}
      {guide.steps.length > 0 ? (
        <ol className="fsp-install-steps">
          {guide.steps.map((s) => (
            <li key={s.text}>
              {s.glyph !== undefined ? (
                <span className="fsp-install-glyph" aria-hidden>
                  <Icon name={s.glyph} size="sm" />
                </span>
              ) : null}
              <span>{s.text}</span>
            </li>
          ))}
        </ol>
      ) : null}
      {guide.note !== undefined ? (
        <Text size="sm" tone="secondary" block>
          {guide.note}
        </Text>
      ) : null}
    </Stack>
  );
}
